import { useState, useEffect } from "react";
import { NonRefundableConfirmModal } from "../components/NonRefundableConfirmModal";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { ReferralLeaderboard } from "../components/ReferralLeaderboard";
import { CreatorEarningsCard } from "../components/community/CreatorEarningsCard";
import { 
  Coins, 
  Sparkles, 
  Image, 
  Video, 
  Wand2, 
  CheckCircle2,
  Clock,
  TrendingUp,
  Zap,
  ChevronRight,
  Loader2,
  History,
  Gift,
  Users,
  Copy,
  Check,
  AlertTriangle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TokensPage = () => {
  const { token, user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  // Free-tier extension state — fetched on mount
  const [extension, setExtension] = useState(null); // {eligible, claimed_at, extension_tokens, reason}
  const [claimingExt, setClaimingExt] = useState(false);
  // Non-refundable consent gate — required before any token-pack checkout
  const [refundConfirm, setRefundConfirm] = useState({ open: false, run: null, label: "" });
  const [tokenData, setTokenData] = useState({
    balance: 0,
    usedThisMonth: 0,
    monthlyAllocation: 25,
    tier: "free"
  });
  const [packs, setPacks] = useState([]);
  const [costs, setCosts] = useState({});
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchTokenData();
    fetchExtensionStatus();
    
    // Check for PayPal return
    const purchaseStatus = searchParams.get("token_purchase");
    if (purchaseStatus === "success") {
      // Handle successful return from PayPal
      const orderId = localStorage.getItem("pending_token_order");
      if (orderId) {
        captureOrder(orderId);
      }
    } else if (purchaseStatus === "cancelled") {
      toast.info("Purchase cancelled");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchExtensionStatus = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/tokens/extension/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setExtension(r.data);
    } catch (_) { /* non-fatal */ }
  };

  const claimExtension = async () => {
    if (claimingExt) return;
    setClaimingExt(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/tokens/extension/claim`, {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(r.data?.message || "Extension applied — enjoy +50 tokens!");
      await Promise.all([fetchTokenData(), fetchExtensionStatus(), refreshUser?.()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply extension");
    } finally {
      setClaimingExt(false);
    }
  };

  const fetchTokenData = async () => {
    try {
      const [balanceRes, packsRes, costsRes, historyRes, referralRes] = await Promise.all([
        axios.get(`${API_URL}/api/tokens/balance`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/tokens/packs`),
        axios.get(`${API_URL}/api/tokens/costs`),
        axios.get(`${API_URL}/api/tokens/history`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/tokens/referral`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTokenData({
        balance: balanceRes.data.tokens,
        usedThisMonth: balanceRes.data.tokens_used_this_month,
        monthlyAllocation: balanceRes.data.monthly_allocation,
        tier: balanceRes.data.subscription_tier
      });
      setPacks(packsRes.data.packs);
      setCosts(costsRes.data.costs);
      setHistory(historyRes.data.purchases || []);
      setReferralData(referralRes.data);
    } catch (error) {
      console.error("Error fetching token data:", error);
      toast.error("Failed to load token data");
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (referralData?.referral_link) {
      navigator.clipboard.writeText(referralData.referral_link);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const captureOrder = async (orderId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/tokens/capture-order/${orderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        setTokenData(prev => ({
          ...prev,
          balance: response.data.new_balance
        }));
        localStorage.removeItem("pending_token_order");
        refreshUser();
        fetchTokenData();
      }
    } catch (error) {
      console.error("Error capturing order:", error);
      toast.error("Failed to complete purchase. Please contact support.");
    }
  };

  const handlePurchase = async (packId) => {
    setPurchasing(packId);
    try {
      const response = await axios.post(
        `${API_URL}/api/tokens/create-order`,
        { pack_id: packId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.approval_url) {
        localStorage.setItem("pending_token_order", response.data.order_id);
        window.location.href = response.data.approval_url;
      } else {
        toast.error("Failed to create payment");
      }
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error(error.response?.data?.detail || "Failed to process purchase");
    } finally {
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ink-950" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="tokens-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-ink-950 flex items-center gap-3">
            <Coins className="w-8 h-8 text-amber-400" />
            Tokens
          </h1>
          <p className="text-ink-500 mt-1">
            Purchase tokens to generate more AI content
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowHistory(!showHistory)}
          className="border-ink-200 text-ink-700 hover:bg-ink-100"
          data-testid="toggle-history-btn"
        >
          <History className="w-4 h-4 mr-2" />
          {showHistory ? "Hide History" : "Purchase History"}
        </Button>
      </div>

      {/* Current Balance Card */}
      <Card className="bg-gradient-to-br from-emerald-900/40 to-emerald-950/40 border-emerald-800/50">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-coral-500/20 flex items-center justify-center">
                <Coins className="w-8 h-8 text-coral-500" />
              </div>
              <div>
                <p className="text-sm text-ink-700">Current Balance</p>
                <p className="text-4xl font-bold text-ink-950" data-testid="token-balance">
                  {tokenData.tier === "ultimate" ? "∞" : tokenData.balance.toLocaleString()}
                </p>
                <p className="text-xs text-ink-500">
                  {tokenData.tier === "ultimate" ? "unlimited (admin)" : "tokens available"}
                </p>
              </div>
            </div>
            
            <div className="flex gap-6 flex-wrap">
              <div className="text-center">
                <p className="text-2xl font-semibold text-ink-950">{tokenData.usedThisMonth}</p>
                <p className="text-xs text-ink-500">Used this month</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-semibold text-amber-400">
                  {tokenData.tier === "ultimate" ? "∞" : `+${tokenData.monthlyAllocation}`}
                </p>
                <p className="text-xs text-ink-500">Monthly bonus</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-coral-500 capitalize">{tokenData.tier}</p>
                <p className="text-xs text-ink-500">Current plan</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Community tip earnings — only renders if user has received tips */}
      <CreatorEarningsCard />

      {/* Token Costs */}
      <Card className="bg-white/60 border-ink-200">
        <CardHeader>
          <CardTitle className="text-lg text-ink-950 flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-400" />
            Token Costs
          </CardTitle>
          <CardDescription>How many tokens each action uses</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl bg-ink-50 border border-ink-200 text-center">
              <Image className="w-6 h-6 mx-auto mb-2 text-coral-500" />
              <p className="text-2xl font-bold text-ink-950">{costs.image_generation || 5}</p>
              <p className="text-xs text-ink-500">Image Generation</p>
            </div>
            <div className="p-4 rounded-xl bg-ink-50 border border-ink-200 text-center">
              <Wand2 className="w-6 h-6 mx-auto mb-2 text-pink-400" />
              <p className="text-2xl font-bold text-ink-950">{costs.image_editing || 2}</p>
              <p className="text-xs text-ink-500">Image Editing</p>
            </div>
            <div className="p-4 rounded-xl bg-ink-50 border border-ink-200 text-center">
              <Video className="w-6 h-6 mx-auto mb-2 text-blue-400" />
              <p className="text-2xl font-bold text-ink-950">{costs.video_generation || 10}</p>
              <p className="text-xs text-ink-500">Video Generation</p>
            </div>
            <div className="p-4 rounded-xl bg-ink-50 border border-ink-200 text-center">
              <Sparkles className="w-6 h-6 mx-auto mb-2 text-coral-500" />
              <p className="text-2xl font-bold text-ink-950">{costs.video_editing || 2}</p>
              <p className="text-xs text-ink-500">Video Editing</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Free-tier extension — one-time +50 tokens, shown only to eligible
          free users. Hidden once claimed or for paid tiers. */}
      {extension?.is_free_tier && (
        <Card
          className={`relative bg-gradient-to-br from-citrus-500/10 via-white to-coral-500/10 border-2 ${
            extension.eligible ? "border-citrus-500/50" : "border-ink-200"
          }`}
          data-testid="free-extension-card"
        >
          <CardContent className="p-6 flex flex-col sm:flex-row items-center gap-4">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-ink-950 text-citrus-500 shrink-0">
              <Gift className="w-7 h-7" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-coral-500">
                One-time free extension
              </p>
              <h3 className="text-lg font-display font-bold text-ink-950 mt-0.5">
                Apply for +{extension.extension_tokens} free tokens
              </h3>
              <p className="text-sm text-ink-500 mt-1">
                {extension.eligible
                  ? "Use this once on us — gives you enough to fully judge if XEA is for you. After this, you'll need to upgrade for more."
                  : extension.reason || "You've already used your one-time extension."}
              </p>
            </div>
            <Button
              onClick={claimExtension}
              disabled={!extension.eligible || claimingExt}
              data-testid="claim-extension-btn"
              className="h-12 px-6 bg-ink-950 hover:bg-coral-500 text-white text-xs font-bold uppercase tracking-[0.18em] disabled:opacity-50 disabled:cursor-not-allowed gap-2"
            >
              {claimingExt && <Loader2 className="w-4 h-4 animate-spin" />}
              {extension.eligible
                ? `Apply +${extension.extension_tokens}`
                : "Already used"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Non-refundable notice — global, sits above token packs since this
          is the "spend money" zone of the app. */}
      <div
        className="flex items-start gap-3 p-4 rounded-2xl bg-rose-50 border border-rose-200"
        data-testid="non-refundable-notice"
      >
        <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-bold text-rose-700 uppercase tracking-[0.12em] text-[10px]">
            Payments are non-refundable
          </p>
          <p className="text-rose-700/90 mt-0.5">
            All token and subscription purchases are final. By completing a purchase you accept
            that no refunds will be issued.
          </p>
        </div>
      </div>

      {/* Token Packs */}
      <div>
        <h2 className="text-xl font-semibold text-ink-950 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-coral-500" />
          Buy Token Packs
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {packs.map((pack) => (
            <Card 
              key={pack.id}
              className={`relative bg-white/60 border-ink-200 hover:border-emerald-600/50 transition-all duration-300 ${
                pack.popular ? "ring-2 ring-ink-950/50" : ""
              } ${pack.best_value ? "ring-2 ring-amber-500/50" : ""}`}
              data-testid={`token-pack-${pack.id}`}
            >
              {pack.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-coral-500 text-ink-950 text-xs font-bold rounded-full">
                  MOST POPULAR
                </div>
              )}
              {pack.best_value && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-ink-950 text-xs font-bold rounded-full">
                  BEST VALUE
                </div>
              )}
              <CardContent className="p-6 pt-8">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-4">
                    <Coins className="w-8 h-8 text-amber-400" />
                  </div>
                  <p className="text-4xl font-bold text-ink-950">{pack.tokens}</p>
                  <p className="text-ink-500">tokens</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm text-ink-700">
                    <CheckCircle2 className="w-4 h-4 text-coral-500" />
                    <span>Never expires</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-ink-700">
                    <CheckCircle2 className="w-4 h-4 text-coral-500" />
                    <span>Rollover to next month</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-ink-700">
                    <CheckCircle2 className="w-4 h-4 text-coral-500" />
                    <span>Use across all features</span>
                  </div>
                </div>
                
                <Button
                  onClick={() => setRefundConfirm({
                    open: true,
                    label: `${pack.name || pack.id} · ${pack.tokens || ""} tokens · $${pack.price || ""}`,
                    run: () => handlePurchase(pack.id),
                  })}
                  disabled={purchasing === pack.id}
                  className={`w-full ${
                    pack.popular 
                      ? "bg-ink-950 hover:bg-ink-900" 
                      : pack.best_value 
                        ? "bg-amber-600 hover:bg-amber-700"
                        : "bg-ink-200 hover:bg-ink-300"
                  }`}
                  data-testid={`buy-pack-${pack.id}-btn`}
                >
                  {purchasing === pack.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Buy for ${pack.price.toFixed(2)}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Monthly Allocation Info */}
      <Card className="bg-white/60 border-ink-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-coral-500/20 flex items-center justify-center flex-shrink-0">
              <Clock className="w-6 h-6 text-coral-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-ink-950 mb-1">Monthly Token Bonus</h3>
              <p className="text-ink-500 text-sm">
                Every month on the 1st, you receive <span className="text-coral-500 font-semibold">{tokenData.monthlyAllocation} free tokens</span> based on your subscription tier. 
                Unused tokens roll over to the next month, so you never lose them!
              </p>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                <div className="p-2 rounded-lg bg-ink-50">
                  <p className="text-xs text-ink-500">Free</p>
                  <p className="font-semibold text-ink-950">25/mo</p>
                </div>
                <div className="p-2 rounded-lg bg-ink-50">
                  <p className="text-xs text-ink-500">Premium</p>
                  <p className="font-semibold text-ink-950">75/mo</p>
                </div>
                <div className="p-2 rounded-lg bg-ink-50">
                  <p className="text-xs text-ink-500">Platinum</p>
                  <p className="font-semibold text-coral-500">150/mo</p>
                </div>
                <div className="p-2 rounded-lg bg-ink-50">
                  <p className="text-xs text-ink-500">Ultimate</p>
                  <p className="font-semibold text-amber-400">500/mo</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referral Program */}
      {referralData && (
        <Card className="bg-gradient-to-br from-amber-900/30 to-orange-950/30 border-amber-800/50">
          <CardHeader>
            <CardTitle className="text-lg text-ink-950 flex items-center gap-2">
              <Gift className="w-5 h-5 text-amber-400" />
              Add a Friend & Both Get Tokens!
            </CardTitle>
            <CardDescription>
              Invite friends and you'll both receive free tokens
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Rewards explanation */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-center">
                <Users className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                <p className="text-2xl font-bold text-amber-400">+50</p>
                <p className="text-sm text-ink-700">tokens for you</p>
              </div>
              <div className="p-4 rounded-xl bg-coral-500/10 border border-coral-500/20 text-center">
                <Gift className="w-8 h-8 mx-auto mb-2 text-coral-500" />
                <p className="text-2xl font-bold text-coral-500">+20</p>
                <p className="text-sm text-ink-700">tokens for your friend</p>
              </div>
            </div>

            {/* Referral Link */}
            <div className="space-y-2">
              <label className="text-sm text-ink-500">Your Referral Link</label>
              <div className="flex gap-2">
                <Input
                  value={referralData.referral_link}
                  readOnly
                  className="bg-ink-100 border-ink-200 text-ink-950"
                  data-testid="referral-link-input"
                />
                <Button
                  onClick={copyReferralLink}
                  className={`px-4 ${copied ? "bg-ink-950" : "bg-amber-600 hover:bg-amber-700"}`}
                  data-testid="copy-referral-btn"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-ink-500">
                Code: <span className="font-mono text-amber-400">{referralData.referral_code}</span>
              </p>
            </div>

            {/* Stats */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-ink-50">
              <div>
                <p className="text-sm text-ink-500">Friends Referred</p>
                <p className="text-2xl font-bold text-ink-950">
                  {referralData.referrals_count} <span className="text-sm font-normal text-ink-500">/ {referralData.max_referrals}</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-ink-500">Tokens Earned</p>
                <p className="text-2xl font-bold text-amber-400">+{referralData.referrals_tokens_earned}</p>
              </div>
            </div>

            {referralData.remaining_referrals <= 0 ? (
              <p className="text-sm text-amber-400 text-center">
                You've reached the maximum referrals. Thanks for spreading the word!
              </p>
            ) : (
              <p className="text-sm text-ink-500 text-center">
                You can invite {referralData.remaining_referrals} more friends
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Referral Leaderboard */}
      <ReferralLeaderboard token={token} />

      {/* Purchase History */}
      {showHistory && (
        <Card className="bg-white/60 border-ink-200">
          <CardHeader>
            <CardTitle className="text-lg text-ink-950 flex items-center gap-2">
              <History className="w-5 h-5 text-ink-500" />
              Purchase History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <div className="text-center py-8 text-ink-500">
                <Coins className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No purchases yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((purchase, idx) => (
                  <div 
                    key={purchase.id || idx}
                    className="flex items-center justify-between p-4 rounded-lg bg-ink-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        purchase.status === "completed" 
                          ? "bg-coral-500/20 text-coral-500" 
                          : "bg-amber-500/20 text-amber-400"
                      }`}>
                        {purchase.status === "completed" ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <Clock className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-ink-950">+{purchase.tokens} Tokens</p>
                        <p className="text-xs text-ink-500">
                          {new Date(purchase.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-ink-950">${purchase.amount?.toFixed(2)}</p>
                      <p className={`text-xs capitalize ${
                        purchase.status === "completed" ? "text-coral-500" : "text-amber-400"
                      }`}>
                        {purchase.status}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Non-refundable confirmation gate */}
      <NonRefundableConfirmModal
        open={refundConfirm.open}
        onOpenChange={(open) => setRefundConfirm((s) => ({ ...s, open }))}
        onConfirm={() => refundConfirm.run?.()}
        itemLabel={refundConfirm.label}
      />
    </div>
  );
};

export default TokensPage;
